// Client half of note search. The server owns the query that reaches the
// database (server/src/services/notes/search.js); this is the same matching
// applied to what the browser already holds -- titles, excerpts, and the body
// of an open note -- so results appear as you type instead of after a round
// trip, and so the highlighting agrees with what matched.
//
// A query is split on whitespace and every term has to appear, in order, with
// anything in between: "meeting notes" matches "meeting with Bob about the
// notes".
const MAX_TERMS = 8
const GAP = 400

const escape = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export function terms(query) {
  if (typeof query !== 'string') return []
  return query.trim().split(/\s+/).filter(Boolean).slice(0, MAX_TERMS)
}

// Matches a whole run of text from the first term to the last, so highlighting
// it would paint everything in between. Used for finding, not for marking.
export function matcher(query) {
  const parts = terms(query).map(escape)
  if (!parts.length) return null
  return new RegExp(parts.join(`[\\s\\S]{0,${GAP}}?`), 'i')
}

export function matches(text, query) {
  const re = matcher(query)
  return !!re && re.test(String(text || ''))
}

// Marks each term separately, so a choppy match highlights the words the user
// typed rather than the whole span between them. Input is plain text; the
// output is HTML, so it is escaped first.
export function highlight(text, query) {
  const plain = String(text == null ? '' : text)
  const parts = terms(query).map(escape)
  const escaped = plain.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
  if (!parts.length) return escaped
  // longest first: "note" must not consume the start of "notebook" and leave
  // the rest of the longer term unmarked
  const re = new RegExp(`(${[...parts].sort((a, b) => b.length - a.length).join('|')})`, 'ig')
  return escaped.replace(re, '<mark>$1</mark>')
}

// Window of plain text around the first term, for a preview that actually
// contains what was searched for. The server sends one for notes it matched on
// their full body; this covers the text the browser already has.
const SNIPPET = 220
const LEAD = 60

export function snippet(text, query) {
  const plain = String(text || '').replace(/\s+/g, ' ').trim()
  const re = matcher(query)
  if (!re) return plain.slice(0, SNIPPET)
  const found = plain.match(re)
  if (!found) return plain.slice(0, SNIPPET)
  const start = Math.max(0, found.index - LEAD)
  return (start > 0 ? '…' : '') + plain.slice(start, start + SNIPPET) + (start + SNIPPET < plain.length ? '…' : '')
}
